import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, Mail, Lock, User, Eye, EyeOff, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin', 'register', 'forgot'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showGoogleHint, setShowGoogleHint] = useState(false); // Show hint when Google account tries password login
  const [processingOAuth, setProcessingOAuth] = useState(false);
  
  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Check for OAuth session_id in URL (fallback if redirected to /auth instead of /auth/callback)
  useEffect(() => {
    const processOAuthFallback = async () => {
      // Check for session_id in hash or query params
      let sessionId = null;
      
      const hash = location.hash;
      if (hash) {
        const hashParams = new URLSearchParams(hash.replace('#', ''));
        sessionId = hashParams.get('session_id');
      }
      
      if (!sessionId) {
        const searchParams = new URLSearchParams(location.search);
        sessionId = searchParams.get('session_id');
      }
      
      if (sessionId) {
        setProcessingOAuth(true);
        
        try {
          const response = await axios.post(
            `${API}/api/auth/google/callback`,
            { session_id: sessionId },
            { withCredentials: true, timeout: 15000 }
          );
          
          if (response.data?.user) {
            login(response.data.user);
            toast.success('Welcome back!');
            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          console.error('OAuth fallback error:', error);
          toast.error('Google sign-in failed. Please try again.');
        } finally {
          setProcessingOAuth(false);
        }
      }
    };
    
    processOAuthFallback();
  }, [location, login, navigate]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    // Use /auth/callback as the redirect URL - this helps with Safari/iPad
    // which sometimes has issues with hash fragments on protected routes
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setShowGoogleHint(false);
    try {
      const response = await axios.post(`${API}/api/auth/login`, {
        email,
        password
      }, { withCredentials: true });
      
      // Use auth context to persist login (cookie set by backend)
      login(response.data.user);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      const errorDetail = error.response?.data?.detail || '';
      
      // Check if this is a Google-only account trying to use password
      if (errorDetail.startsWith('GOOGLE_ACCOUNT:')) {
        const message = errorDetail.replace('GOOGLE_ACCOUNT:', '');
        toast.error(message);
        setShowGoogleHint(true);
      } else if (errorDetail.toLowerCase().includes('invalid email or password')) {
        // Show more helpful message with password reset option
        toast.error('Invalid email or password. Forgot your password?', {
          duration: 5000,
          action: {
            label: 'Reset Password',
            onClick: () => setMode('forgot')
          }
        });
      } else if (errorDetail.toLowerCase().includes('disabled')) {
        toast.error('This account has been disabled. Please contact support.');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your internet connection.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again in a moment.');
      } else {
        toast.error(errorDetail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/register`, {
        first_name: firstName,
        last_name: lastName,
        email,
        password
      }, { withCredentials: true });
      
      // Use auth context to persist login (cookie set by backend)
      login(response.data.user);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      const errorDetail = error.response?.data?.detail || '';
      
      // Check if account already exists
      if (errorDetail.toLowerCase().includes('already exists')) {
        toast.error('An account with this email already exists. Try signing in instead.', {
          duration: 5000,
          action: {
            label: 'Sign In',
            onClick: () => setMode('signin')
          }
        });
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your internet connection.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again in a moment.');
      } else {
        toast.error(errorDetail || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      toast.success('If an account exists, a password reset link has been sent to your email');
      setMode('signin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while processing OAuth
  if (processingOAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#1e3a5f] animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Completing Google sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-center pt-12 pb-6">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center">
            <BarChart3 className="w-7 h-7 text-white" />
          </div>
          <span className="text-2xl font-bold text-[#1e3a5f]">OvertimeIQ</span>
        </div>
      </div>

      {/* Auth Card */}
      <div className="flex-1 flex items-start justify-center px-4 pt-4 pb-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">
              {mode === 'forgot' ? 'Reset Password' : 'Welcome'}
            </h1>
            <p className="text-slate-500">
              {mode === 'forgot' 
                ? 'Enter your email to receive a reset link'
                : 'Sign in to access your analytics dashboard'}
            </p>
          </div>

          {mode !== 'forgot' && (
            <>
              {/* Google Login */}
              <div className={`relative ${showGoogleHint ? 'animate-pulse' : ''}`}>
                {showGoogleHint && (
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full z-10">
                    <div className="bg-[#4CAF93] text-white text-sm px-4 py-2 rounded-lg shadow-lg whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 animate-bounce" style={{ animationDirection: 'alternate' }} />
                        <span>Use this button to sign in</span>
                      </div>
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-[#4CAF93]"></div>
                    </div>
                  </div>
                )}
                <Button
                  variant="outline"
                  className={`w-full mb-6 py-6 border-slate-200 hover:bg-slate-50 transition-all ${
                    showGoogleHint ? 'ring-2 ring-[#4CAF93] ring-offset-2 border-[#4CAF93] bg-green-50' : ''
                  }`}
                  onClick={handleGoogleLogin}
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </div>

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-slate-400 uppercase tracking-wider">
                    or continue with email
                  </span>
                </div>
              </div>

              {/* Mode Toggle */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl mb-6">
                <button
                  className={`py-3 px-4 rounded-lg font-medium transition-all ${
                    mode === 'signin'
                      ? 'bg-white shadow-sm text-[#1e3a5f]'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  onClick={() => setMode('signin')}
                >
                  Sign In
                </button>
                <button
                  className={`py-3 px-4 rounded-lg font-medium transition-all ${
                    mode === 'register'
                      ? 'bg-white shadow-sm text-[#1e3a5f]'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  onClick={() => setMode('register')}
                >
                  Register
                </button>
              </div>
            </>
          )}

          {/* Sign In Form */}
          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 py-6 bg-slate-50 border-slate-200"
                    data-testid="signin-email"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 py-6 bg-slate-50 border-slate-200"
                    data-testid="signin-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className="text-sm text-[#2E5C8A] hover:text-[#1e3a5f]"
                  onClick={() => setMode('forgot')}
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                className="w-full py-6 bg-[#1e3a5f] hover:bg-[#2E5C8A] text-white"
                disabled={loading}
                data-testid="signin-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Register Form */}
          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-2">First Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="pl-10 py-6 bg-slate-50 border-slate-200"
                      data-testid="register-firstname"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Last Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="pl-10 py-6 bg-slate-50 border-slate-200"
                      data-testid="register-lastname"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 py-6 bg-slate-50 border-slate-200"
                    data-testid="register-email"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 py-6 bg-slate-50 border-slate-200"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 py-6 bg-slate-50 border-slate-200"
                    data-testid="register-confirm-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full py-6 bg-[#4CAF93] hover:bg-[#3d9b7d] text-white"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 py-6 bg-slate-50 border-slate-200"
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full py-6 bg-[#1e3a5f] hover:bg-[#2E5C8A] text-white"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Send Reset Link
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>

              <button
                type="button"
                className="w-full text-center text-[#2E5C8A] hover:text-[#1e3a5f] flex items-center justify-center gap-2"
                onClick={() => setMode('signin')}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </form>
          )}

          {/* Terms */}
          {mode !== 'forgot' && (
            <p className="mt-6 text-center text-xs text-slate-400">
              By signing in, you agree to our{' '}
              <a href="#" className="text-[#2E5C8A] hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-[#2E5C8A] hover:underline">Privacy Policy</a>
            </p>
          )}
        </div>
      </div>

      {/* Back to Home */}
      <div className="text-center pb-8">
        <button
          className="text-[#2E5C8A] hover:text-[#1e3a5f] flex items-center justify-center gap-2 mx-auto"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>
      </div>
    </div>
  );
};

export default AuthPage;
